import { CONTACT_US_COPY } from './contact-us.copy';

/** Must match ironman MiscController.CONTACT_US_MAX_ATTACHMENT_BYTES (10 MB in total). */
export const CONTACT_US_MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** Field order is DOM order: the first invalid field in this list gets focus. */
export const CONTACT_US_FIELDS = ['email', 'message', 'attachment', 'captcha'] as const;
export type ContactUsField = typeof CONTACT_US_FIELDS[number];
export type ContactUsFieldErrors = Partial<Record<ContactUsField, string>>;

/** Same shape the server accepts as a Reply-To (MiscController.contactUsReplyTo), so "we'll reply" is never a lie. */
const EMAIL_SHAPE = /^[^\s@<>(),;:"[\]]+@[^\s@<>(),;:"[\]]+\.[^\s@<>(),;:"[\]]+$/;

export function isUsableEmail(email: string): boolean {
    return email.length <= 254 && EMAIL_SHAPE.test(email);
}

export interface ContactUsInput {
    email: string;
    message: string;
    token: string | null | undefined;
    attachmentBytes: number;
}

export function validateContactUs(input: ContactUsInput): ContactUsFieldErrors {
    const errors: ContactUsFieldErrors = {};
    if (input.email.length > 0 && !isUsableEmail(input.email)) errors.email = CONTACT_US_COPY['X4-2'];
    if (input.message.trim().length === 0) errors.message = CONTACT_US_COPY['X4-1'];
    if (input.attachmentBytes > CONTACT_US_MAX_ATTACHMENT_BYTES) errors.attachment = CONTACT_US_COPY['X4-12'];
    if (!input.token) errors.captcha = CONTACT_US_COPY['X4-3'];
    return errors;
}

export function firstInvalidField(errors: ContactUsFieldErrors): ContactUsField | null {
    return CONTACT_US_FIELDS.find((field) => !!errors[field]) ?? null;
}

export type ContactUsOutcome = 'sent' | 'failed' | 'rate_limited' | 'too_large';

/**
 * QA1-02: edges before the fix answered a JSON string literal ("{\"error\": false}") for a delivered message. Up to two
 * string layers are parsed so such a body is read as the object it carries; anything unparsable stays a string.
 */
function unwrapJsonString(result: unknown): unknown {
    let value = result;
    for (let depth = 0; depth < 2 && typeof value === 'string'; depth++) {
        try {
            value = JSON.parse(value);
        } catch {
            return value;
        }
    }
    return value;
}

/** A 200 answer: only an explicit `error: false` is a sent message. */
export function outcomeOfResponse(result: unknown): ContactUsOutcome {
    const value = unwrapJsonString(result);
    return value != null && typeof value === 'object' && (value as { error?: unknown }).error === false ? 'sent' : 'failed';
}

/** A failed request (LightHttp error: status 0 = network, 4xx ClientError, 5xx ServiceError). */
export function outcomeOfError(error: unknown): ContactUsOutcome {
    const status = (error as { status?: number } | null)?.status;
    if (status === 429) return 'rate_limited';
    if (status === 413) return 'too_large';
    return 'failed';
}
